import { ref, watch, type Ref, onBeforeUnmount } from "vue";
import { isComponentScope } from "../component";
import type { Pagination } from "@/types/pagination";
import { usePaginator, type UsePaginator } from "../hooks/usePaginator";
import { useDebounced } from "../hooks/useDebounced";


type AsyncFun<T = any> = (pagination: Pagination) => Promise<T[]>;

export type UseAsyncListOptions = {
  pagination?: Pagination;
  debounce?: number;
  immediate?: boolean;
}

export type UseAsyncList<T = any> = {
  items: Ref<T[]>,
  loading: Ref<boolean>;
  error: Ref<InstanceType<typeof Error> | Error | unknown | undefined>
  refresh: () => Promise<void>;
  paginator: UsePaginator;
}

export const useAsyncList = <T = any>(fun: AsyncFun<T>, deps?: any[], options?: UseAsyncListOptions): UseAsyncList<T> => {
  const items: Ref<T[]> = ref([]);
  const loading: Ref<boolean> = ref(true);
  const error: Ref<InstanceType<typeof Error> | Error | unknown | undefined> = ref(undefined);
  const counter: Ref<number> = ref(0);

  const paginator = usePaginator({
    pagination: options?.pagination,
    onChange: () => {
      refresh()
    }
  })

  const fetchItems = async () => {
    loading.value = true;
    try {
      items.value = (await fun(paginator.pagination.value)) || [];
    } catch (e) {
      error.value = e;
      console.error(e);
    }
    loading.value = false;
  }

  const fetchItemsDebounced = options?.debounce ? useDebounced(fetchItems, options.debounce) : fetchItems;

  const unwatch = watch([...(deps || []), counter], async () => {
    await fetchItemsDebounced();
  }, { immediate: options?.immediate === false ? false : true });

  if (isComponentScope()) {
    onBeforeUnmount(unwatch);
  }

  const refresh = async () => {
    await fetchItemsDebounced();
    //counter.value += 1;
  }


  return { items, loading, error, refresh, paginator };
}
